import { HttpClient } from '@angular/common/http';
import {
	Component,
	computed,
	DestroyRef,
	inject,
	OnDestroy,
	OnInit,
	signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@app/_modules/translate/translate.service';
import { AuthService, ModalService, SettingsService } from '@app/_services';
import { environment } from '@env/environment';
import {
	filter,
	fromEvent,
	merge,
	of,
	Subscription,
	switchMap,
	tap,
} from 'rxjs';

@Component({
	selector: 'sessionExpiration',
	templateUrl: 'sessionExpiration.template.html',
	styleUrls: ['sessionExpiration.styles.scss'],
	standalone: false,
})
export class SessionExpirationComponent implements OnInit, OnDestroy {
	public modalService = inject(ModalService);
	private readonly auth = inject(AuthService);
	private readonly translate = inject(TranslateService);
	private readonly http = inject(HttpClient);
	private readonly settings = inject(SettingsService);
	private readonly destroyRef = inject(DestroyRef);

	public countDownTime: number; /* Comes from translations. See ngOnInit */
	public timeLeft = signal(0);
	public timeout;
	public counterInterval;
	public modalTitle: string = this.translate.get('session.expiring');
	public renewLoader: boolean = false;
	private loginSubscription: Subscription;
	// How many seconds to increase on click/key press
	private readonly sessionExtendThreshold = 300;

	private readonly expirationTimes = computed(() => {
		const expiration = this.auth.expireTime();
		let sessionExpiration = this.auth.expireTime();
		// Allow disabling of 50 min subtraction in dev
		if (
			environment.DEV_AUTH !== true ||
			!globalThis?.localStorage?.getItem('old_session_time')
		) {
			// Subtracting 50 mins to reduce session duration
			sessionExpiration -= 3000 * 1000;
		}

		return { expiration, sessionExpiration };
	});

	public logOut(): void {
		this.auth.logout();
		clearTimeout(this.timeout);
		clearInterval(this.counterInterval);
	}

	public renewLogin(): void {
		this.renewLoader = true;
		this.http
			.get(`${this.settings.url}/api/v1/token-renew?_format=json`)
			.subscribe((response) => {
				if (response['token']) {
					this.auth.refreshUser(response['token']);
					this.modalService.close('sessionExpirationModal');
					this.checkExpiration();
				} else {
					this.logOut();
					clearTimeout(this.timeout);
					clearInterval(this.counterInterval);
				}
				this.renewLoader = false;
			});
	}

	ngOnInit() {
		this.countDownTime = parseInt(this.translate.get('session.timeout')) || 100;
		this.watchLogin();
		if (globalThis?.window) {
			// Extend time if user is clicking/typing
			this.auth.isAuthenticated
				.pipe(
					switchMap((val) => {
						if (!val) {
							return of();
						}
						return merge(
							fromEvent(document, 'click'),
							fromEvent(document, 'keydown'),
						);
					}),
					filter((val) => !!val),
					tap(() => {
						// Make sure counter is not already running
						if (
							this.timeLeft() > 100 &&
							this.timeLeft() <= this.sessionExtendThreshold
						) {
							const currTime = Date.now();
							if (
								this.expirationTimes().expiration - currTime >
								this.sessionExtendThreshold * 1000
							) {
								this.timeLeft.update(
									(prev) => prev + this.sessionExtendThreshold,
								);
								this.setModalTimeout();
							} else {
								this.renewLogin();
							}
						}
					}),
					takeUntilDestroyed(this.destroyRef),
				)
				.subscribe();
		}
	}

	ngOnDestroy() {
		this.loginSubscription.unsubscribe();
	}

	private count(): void {
		const expires = this.expirationTimes().sessionExpiration;
		clearInterval(this.counterInterval);
		this.timeLeft.set(this.countDownTime);
		const expirationTime = new Date();
		// Set an actual timestamp for the timeout, countdown time is in seconds
		expirationTime.setTime(
			expirationTime.getTime() + this.countDownTime * 1000,
		);
		if (expires) {
			this.counterInterval = setInterval(() => {
				// Check how much real time has passed, can't just decrement a value as inactive tabs get suspended etc
				this.timeLeft.set(
					Math.round((expirationTime.getTime() - new Date().getTime()) / 1000),
				);
				if (this.timeLeft() <= 0) {
					this.showLoginMessage();
				}
			}, 1000);
		} else {
			this.timeLeft.set(0);
		}
	}

	private showLoginMessage(): void {
		this.logOut();
		clearInterval(this.counterInterval);
		this.modalTitle = this.translate.get('session.expired');
		if (!this.modalService.isOpen('sessionExpirationModal')) {
			this.modalService.open('sessionExpirationModal');
		}
	}

	private checkExpiration(): void {
		const expires = this.expirationTimes().sessionExpiration;
		clearTimeout(this.timeout);
		clearInterval(this.counterInterval);

		if (expires) {
			const currentTime = new Date().getTime();
			this.timeLeft.set(Math.ceil((expires - currentTime) / 1000));

			/* Debug timer */
			const sessionTest = window.location.href.match('sessionTest');

			if (sessionTest) {
				this.timeLeft.set(this.countDownTime + 2);
			}
			this.setModalTimeout();
		}
	}

	private setModalTimeout() {
		clearTimeout(this.timeout);
		this.timeout = setTimeout(
			() => {
				this.count();
				this.modalService.open('sessionExpirationModal');
			},
			(this.timeLeft() - this.countDownTime) * 1000,
		);
	}

	private watchLogin(): void {
		this.loginSubscription = this.auth.isAuthenticated.subscribe((response) => {
			if (response) {
				this.checkExpiration();
			} else {
				clearTimeout(this.timeout);
				clearInterval(this.counterInterval);
			}
		});
	}
}
